// Pure helpers that build an <img sizes> value from a layout (no React). The provider lives in
// @core/helpers/client/contexts/ImageSizesContext.
//
// Without a fitting `sizes` the browser picks the image file for a 886px box even when the card is 150px wide; with
// it, a card in a 5-per-row slider gets a ~20vw file. Values may overestimate a little (gaps and paddings are ignored),
// never underestimate, so an image is never softer than its box.

const vw = (count) => `${Math.round((100 / count) * 10) / 10}vw`;

/**
 * `sizes` for an image that fills one column of a full-width layout with `columns` per breakpoint:
 * { 0: 1, 768: 3, 1280: 5 } (min-width in px → columns). A layout narrower than the viewport passes its own string.
 */
export const columns_image_sizes = (columns = {}) => {
	const steps = Object.entries(columns)
		.map(([min_width, count]) => [Number(min_width), Number(count)])
		.filter(([min_width, count]) => Number.isFinite(min_width) && count > 0)
		.sort((a, b) => b[0] - a[0]);
	if (!steps.length) return undefined;
	const base = steps.find(([min_width]) => min_width === 0);
	const parts = steps
		.filter(([min_width]) => min_width > 0)
		.map(([min_width, count]) => `(min-width:${min_width}px) ${vw(count)}`);
	parts.push(vw(base ? base[1] : steps[steps.length - 1][1]));
	return parts.join(", ");
};

/**
 * `sizes` for an image that fills one slide of a Swiper config ({ slidesPerView, breakpoints }). `auto` slides keep
 * their own width, so no value is returned for them (the component's default applies).
 */
export const slider_image_sizes = ({ slidesPerView, breakpoints } = {}) => {
	const per_view = (value) => (value === "auto" ? NaN : Number(value ?? 1));
	const columns = { 0: per_view(slidesPerView) };
	for (const [min_width, bp] of Object.entries(breakpoints || {})) {
		if (bp?.slidesPerView !== undefined) columns[min_width] = per_view(bp.slidesPerView);
	}
	if (Object.values(columns).some((count) => !(count > 0))) return undefined;
	return columns_image_sizes(columns);
};
